import { useEffect, useMemo, useRef, useState, useId } from 'react'
import { Elements, RecurlyProvider, useRecurly } from '@recurly/react-recurly'
import type * as Recurly from '@recurly/recurly-js'
import classNames from 'classnames'
import { When } from 'react-if'
import { Button } from '@/atoms/Button'
import { CreditCardIcon } from '@/atoms/Icons/CreditCardIcon'
import { WarningCircleFillIcon } from '@/atoms/Icons/WarningCircleFillIcon'
import { CaptionMD, ParagraphLG, ParagraphSM, TitleLG } from '@/atoms/Text'
import { GUILD_OFFER_RECURLY_PLAN_MAP } from '@/constants/recurly-plans'
import { NextPageWithLayout } from '@/layouts/types'
import { Offer } from '@/services/PifService'
import { createRecurlyAccountSubscription } from '@/services/Recurly/RecurlyService'
import { useUser, User } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { getRecurlyPublicKey } from '@/utils/environment-variables'
import { logger } from '@/utils/logging'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

interface PaypalCheckoutEmbedProps {
  offer: Offer
  onApproved?: () => void | Promise<void>
  onProcessingChange?: (processing: boolean) => void
  canShowCheckoutOptions?: boolean
  onCardPaymentSelected?: () => void
}

export const PaypalCheckoutEmbed: NextPageWithLayout<PaypalCheckoutEmbedProps> = ({
  offer,
  onApproved,
  onProcessingChange,
  canShowCheckoutOptions = false,
  onCardPaymentSelected,
}) => {
  const { user } = useUser()
  const { t } = useTranslate('guild')
  const planId = GUILD_OFFER_RECURLY_PLAN_MAP[offer.id]
  const [paypalError, setPaypalError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [checkoutUser, setCheckoutUser] = useState<User | null>(null)

  // NOTE: Recurly public key is required to initialize the RecurlyProvider
  const recurlyPublicKey = useMemo(() => getRecurlyPublicKey() ?? '', [])

  useEffect(() => {
    if (user && !checkoutUser) {
      setCheckoutUser(user)
    }
  }, [user, checkoutUser])

  // If we don't have the essentials, don't render at all. Promo mismatch will not unmount the SDK; we'll just hide it.
  if (!planId || !recurlyPublicKey) {
    return null
  }

  return (
    <div className="w-full px-6 md:px-12 lg:px-16">
      <div
        className={classNames(
          'mb-4 flex w-full flex-col items-center justify-center gap-2 lg:mb-8 lg:flex-row lg:gap-4',
          { 'lg:!flex-col': canShowCheckoutOptions },
        )}
      >
        {canShowCheckoutOptions && (
          <TitleLG weight="bold" className="se:photon-title-md mb-8 mt-2 lg:mb-4 lg:mt-8" color="core-gray-950">
            {t('chooseYourPaymentMethod', 'Choose your payment method')}
          </TitleLG>
        )}
        {canShowCheckoutOptions ? (
          <Button
            variant="black"
            className="flex min-w-[300px] flex-row gap-2 rounded-full !bg-core-gray-800"
            onClick={onCardPaymentSelected}
          >
            {t('creditOrDebitCard', 'Credit or Debit Card')}
            <CreditCardIcon color="white" size={20} />
          </Button>
        ) : (
          <ParagraphLG weight="bold">Check out on Angel below</ParagraphLG>
        )}
        <div className="relative flex h-8 w-full flex-col items-center justify-center lg:w-[150px]">
          <div className="absolute top-4 h-[2px] w-full bg-core-gray-200 lg:w-[150px]"></div>
          <ParagraphSM className="relative bg-white px-2" color="core-gray-600">
            or
          </ParagraphSM>
        </div>
        <div className="min-h-[42px] w-[300px] lg:w-1/3">
          <When condition={checkoutUser !== null && checkoutUser !== undefined}>
            {/* Keep the PayPal subtree mounted even on promo mismatch, but visually hide and disable interaction. */}
            <div className="relative !max-h-[42px] min-h-[42px]" aria-busy={isProcessing}>
              <RecurlyProvider publicKey={recurlyPublicKey}>
                <Elements>
                  <RecurlyPayPal
                    onApproved={onApproved}
                    setPaypalError={setPaypalError}
                    tErrorMessage={t(
                      'paypalPaymentUnsuccessful',
                      'An unexpected error occurred with PayPal. Please try again or use another payment method.',
                    )}
                    planCode={planId}
                    user={checkoutUser as User}
                    onProcessingChange={(val) => {
                      setIsProcessing(val)
                      onProcessingChange?.(val)
                    }}
                    offer={offer}
                  />
                </Elements>
              </RecurlyProvider>
              {isProcessing ? (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/90">
                  <div className="pointer-events-none flex flex-col items-center">
                    <span className="sr-only">Processing</span>
                    {/* Simple visual indicator without adding dependencies */}
                    <div className="h-16 w-16 animate-spin rounded-full border-4 border-core-gray-300 border-t-core-oxide" />
                    <ParagraphSM className="mt-3" color="core-gray-700">
                      {t('processingPayment', 'Processing Payment')}
                    </ParagraphSM>
                  </div>
                </div>
              ) : null}
            </div>
          </When>
        </div>
      </div>
      {paypalError ? (
        <div
          role="alert"
          aria-live="polite"
          className="mb-2 flex gap-2 rounded-xl border-2 border-[#9E240C] bg-error-100 p-4 lg:mb-8"
        >
          <WarningCircleFillIcon color="error-700" className="shrink-0" />
          <CaptionMD color="error-700">{paypalError}</CaptionMD>
        </div>
      ) : null}
    </div>
  )
}

// Minimal Recurly PayPal integration stub using the useRecurly hook
// Renders a PayPal button via Recurly PayPal Complete and wires basic handlers.
const RecurlyPayPal = ({
  onApproved,
  setPaypalError,
  tErrorMessage,
  planCode,
  user,
  onProcessingChange,
  offer,
}: {
  onApproved?: () => void | Promise<void>
  setPaypalError: (msg: string | null) => void
  tErrorMessage: string
  planCode: string
  user: User
  onProcessingChange?: (processing: boolean) => void
  offer: Offer
}) => {
  const recurly = useRecurly()
  const track = useSafeTrack()
  const { sale } = useGuildSignupContext()

  // Use library types: model the PayPal instance as the return type of Recurly.Recurly['PayPal']
  type PayPalInstance = ReturnType<Recurly.Recurly['PayPal']>

  // Track the created PayPal instance so we don't initialize more than once
  const paypalInstanceRef = useRef<PayPalInstance | null>(null)
  // Defer first initialization to next tick to avoid early teardown/race conditions
  const initTimeoutRef = useRef<number | null>(null)
  // Use a React-generated id that is stable across SSR/CSR to prevent hydration mismatches.
  // IMPORTANT: useId may include characters like ':' which break document.querySelector('#id').
  // Recurly forwards our target to querySelector, so we must ensure a CSS-selector-safe id.
  const reactId = useId()
  const safeReactId = useMemo(() => reactId.replace(/[^a-zA-Z0-9_-]/g, '-'), [reactId])
  const containerId = `recurly-paypal-button-${safeReactId}`
  // Keep latest handlers/messages without re-initializing the PayPal instance
  const onApprovedRef = useRef(onApproved)
  const tErrorMessageRef = useRef(tErrorMessage)
  const setPaypalErrorRef = useRef(setPaypalError)
  const planCodeRef = useRef(planCode)
  const userRef = useRef(user)
  const offerRef = useRef(offer)
  const trackRef = useRef(track)
  const onProcessingChangeRef = useRef(onProcessingChange)
  const couponCodeRef = useRef<string | undefined>(undefined)

  // Keep latest setPaypalError without re-binding handlers
  useEffect(() => {
    setPaypalErrorRef.current = setPaypalError
  }, [setPaypalError])

  // Keep latest dependencies in refs
  useEffect(() => {
    trackRef.current = track
  }, [track])

  useEffect(() => {
    onProcessingChangeRef.current = onProcessingChange
  }, [onProcessingChange])

  // Compute and store the latest couponCode from the current sale promotions for the selected offer
  useEffect(() => {
    try {
      const code = sale?.promotions?.find((p) => p.offer.id === offerRef.current?.id)?.promotionCode?.code
      couponCodeRef.current = code || undefined
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger().error('Failed to resolve coupon code from sale promotions', { message: error.message, name: error.name })
      couponCodeRef.current = undefined
    }
  }, [sale])

  // Stable wrapper that defers to the latest refs (prevents stale closures and unnecessary re-inits)
  const handleTokenRef = useRef<(token: Recurly.TokenPayload) => Promise<void>>()
  useEffect(() => {
    handleTokenRef.current = async (token: Recurly.TokenPayload) => {
      onProcessingChangeRef.current?.(true)
      setPaypalErrorRef.current(null)
      try {
        await createRecurlyAccountSubscription({
          email: userRef.current?.email ?? '',
          token: token.id,
          planCode: planCodeRef.current,
          userId: userRef.current?.id ?? '',
          couponCode: couponCodeRef.current,
        })

        const cb = onApprovedRef.current
        if (cb) {
          // Defer to next tick to stay out of Recurly event stack
          setTimeout(() => {
            void cb()
          }, 0)
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        logger().error(
          'Guild PayPal Recurly subscription creation failed',
          { message: error.message, name: error.name, planCode: planCodeRef.current, userId: userRef.current?.id },
          error,
        )
        setPaypalErrorRef.current(tErrorMessageRef.current)
      } finally {
        onProcessingChangeRef.current?.(false)
      }
    }
  }, [])

  useEffect(() => {
    onApprovedRef.current = onApproved
  }, [onApproved])

  useEffect(() => {
    tErrorMessageRef.current = tErrorMessage
  }, [tErrorMessage])

  useEffect(() => {
    planCodeRef.current = planCode
  }, [planCode])

  useEffect(() => {
    userRef.current = user
  }, [user])

  useEffect(() => {
    offerRef.current = offer
  }, [offer])

  // Create and bind PayPal integration once when Recurly is ready
  useEffect(() => {
    // Guard in case Recurly has not initialized yet
    if (!recurly) return
    // Avoid initializing multiple times on re-renders or if an init is already queued
    if (paypalInstanceRef.current || initTimeoutRef.current !== null) return

    // Defer first render to next tick to avoid PayPal/zoid teardown during React commit
    initTimeoutRef.current = window.setTimeout(() => {
      initTimeoutRef.current = null
      // Double-check not already initialized
      if (paypalInstanceRef.current) return

      // Ensure the container exists and is clean before rendering
      if (typeof document !== 'undefined') {
        const el = document.getElementById(containerId)
        if (!el) {
          return
        }
        // Clear any orphaned content left by hot reloads or prior mounts
        el.innerHTML = ''
      }

      // Create a new PayPal instance using PayPal Complete and target a container for the button
      const paypal = (recurly as unknown as { PayPal: (options: unknown) => PayPalInstance }).PayPal({
        payPalComplete: {
          target: `#${containerId}`,
          buttonOptions: {
            style: {
              shape: 'pill',
              height: 42,
              overflow: 'hidden',
              borderRadius: 9999,
            },
            onCancel: () => {
              setPaypalErrorRef.current(null)
            },
          },
        },
      })

      paypalInstanceRef.current = paypal

      // Wrap event handlers to always use the latest refs
      paypal.on('token', (token: Recurly.TokenPayload) => {
        // Delegate to latest implementation
        void handleTokenRef.current?.(token)
      })

      paypal.on('cancel', () => {
        setPaypalErrorRef.current(null)
        void trackRef.current('Guild Checkout Cancelled', {
          provider: 'paypal',
          offer: JSON.stringify(offerRef.current),
          planId: planCodeRef.current,
        })
      })

      paypal.on('error', (err: Error) => {
        logger().error('Guild PayPal checkout error (Recurly)', { message: err.message, name: err.name }, err)
        setPaypalErrorRef.current(tErrorMessageRef.current)
        void trackRef.current('Guild Checkout Failed', {
          provider: 'paypal',
          offer: JSON.stringify(offerRef.current),
          planId: planCodeRef.current,
          errorMessage: err.message,
          errorName: err.name,
        })
      })
    }, 0)

    return () => {
      // Cancel any pending init if we unmount too quickly
      if (initTimeoutRef.current !== null) {
        clearTimeout(initTimeoutRef.current)
        initTimeoutRef.current = null
      }
      try {
        // Let the SDK handle cleanup of its own frames; avoid manual DOM teardown
        paypalInstanceRef.current?.destroy?.()
      } catch (e) {
        // Defensive: some versions may throw if already destroyed
      } finally {
        paypalInstanceRef.current = null
      }
    }
  }, [recurly, containerId])

  // Container where Recurly will render the PayPal button
  // Force a lower z-index to prevent PayPal SDK frames from appearing above modals
  return (
    <div
      id={containerId}
      className="relative z-0 max-h-[42px] min-h-[42px] w-full !overflow-hidden !rounded-full bg-[#F4bb36]"
    />
  )
}
